"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.AsyncLock = void 0;
exports.decodeJWT = decodeJWT;
exports.isTokenValid = isTokenValid;
var _jwtDecode = require("jwt-decode");
function decodeJWT(token) {
  try {
    const decoded = (0, _jwtDecode.jwtDecode)(token);
    if (decoded && typeof decoded === "object" && "exp" in decoded) {
      return decoded;
    }
    return null;
  } catch (error) {
    return null;
  }
}
function isTokenValid(decodedToken, bufferInSeconds) {
  const currentTimeInSeconds = Math.floor(Date.now() / 1000); // Current time in seconds
  const bufferTime = bufferInSeconds; // 60 seconds buffer

  // Check if token will be valid for at least next 60 seconds
  // exp - Unix timestamp in UTC timezone in seconds
  return decodedToken.exp > currentTimeInSeconds + bufferTime;
}

/**
 * AsyncLock provides a way to synchronize async operations
 * with acquire/release semantics and timeout capabilities.
 */
class AsyncLock {
  locked = false;
  waitQueue = [];
  autoReleaseTimer = null;

  /**
   * Check if the lock is currently acquired
   */
  isLocked() {
    return this.locked;
  }

  /**
   * Acquire the lock
   * @param acquireTimeout Optional timeout in ms after which the acquire promise rejects
   * @param holdDuration Optional duration in ms after which the lock auto-releases
   * @returns Promise that resolves when lock is acquired, or rejects on timeout
   */
  async acquire(acquireTimeout, holdDuration) {
    // If lock is free, acquire it immediately
    if (!this.locked) {
      this.locked = true;
      if (holdDuration) {
        this.setupAutoRelease(holdDuration);
      }
      return;
    }

    // Otherwise wait for lock to be released
    await new Promise((resolve, reject) => {
      const waiter = {
        resolve: value => {
          if (value) {
            if (holdDuration) {
              this.setupAutoRelease(holdDuration);
            }
            resolve();
          } else {
            reject(new Error("Failed to acquire lock"));
          }
        },
        reject,
        timer: null
      };
      if (acquireTimeout) {
        waiter.timer = setTimeout(() => {
          // Remove from queue
          const index = this.waitQueue.indexOf(waiter);
          if (index !== -1) {
            this.waitQueue.splice(index, 1);
          }
          reject(new Error(`Lock acquire timeout after ${acquireTimeout}ms`));
        }, acquireTimeout);
      }
      this.waitQueue.push(waiter);
    });
  }

  /**
   * Release the lock and notify waiters
   */
  release() {
    if (!this.locked) {
      return;
    }
    this.clearAutoReleaseTimer();
    if (this.waitQueue.length > 0) {
      // Give lock to next waiter
      const waiter = this.waitQueue.shift();
      if (waiter.timer) {
        clearTimeout(waiter.timer);
      }
      waiter.resolve(true);
    } else {
      // No waiters, just release the lock
      this.locked = false;
    }
  }

  /**
   * Setup auto-release timer for the lock
   * @param duration Duration in ms after which the lock will auto-release
   */
  setupAutoRelease(duration) {
    this.clearAutoReleaseTimer();
    this.autoReleaseTimer = setTimeout(() => {
      this.release();
    }, duration);
  }

  /**
   * Clear the auto-release timer if it exists
   */
  clearAutoReleaseTimer() {
    if (this.autoReleaseTimer) {
      clearTimeout(this.autoReleaseTimer);
      this.autoReleaseTimer = null;
    }
  }
}
exports.AsyncLock = AsyncLock;